import { useEffect, useMemo, useRef, useState } from "react";
import {
  Grid,
  GridNoRecords,
  GridColumn,
  type GridHandle,
  type GridProps,
  type GridColumnProps,
  type GridFilterChangeEvent,
  type GridDataStateChangeEvent,
} from "@progress/kendo-react-grid";
import {
  process,
  type DataResult,
  type FilterDescriptor,
  type SortDescriptor,
  type State as GridDataState,
  type CompositeFilterDescriptor,
} from "@progress/kendo-data-query";
import Alert from "@/components/shared/Alert";
import AccessiblePager from "@/components/shared/tables/AccessiblePager";
import { joinClasses } from "@/utils/styling";
import { useControlledSwitchWarning } from "@/hooks/shared";
import {
  rewriteDateEqualsFilter,
  convertKendoTableDates,
} from "./kendo-filter-utils";
import {
  DefaultDataCell,
  useEnterActivatableDataCell,
} from "./kendo-cell-utils";

type TableProps<T> = Pick<
  GridProps,
  "data" | "showLoader" | "onRowClick" | "onDataStateChange" | "autoProcessData"
> & {
  data: T[];
  columns: GridColumnProps[];
  error?: Error | null;
  noItemsMessage?: string;
  // presence of dataState prop determines if data state (filtering, sorting, paging)
  // is controlled by parent or managed internally by this component
  dataState?: GridDataState;
  initialSkip?: number;
  initialTake?: number;
  initialFilter?: CompositeFilterDescriptor;
  initialSort?: SortDescriptor[];
  filterable?: boolean;
};

const DEFAULT_PAGE_SIZE = 10;
const DEFAULT_START_INDEX = 0;
const DEFAULT_MIN_COLUMN_WIDTH = 200; // px

export default function KendoTableClientState<T>({
  data,
  showLoader = false,
  onRowClick,
  onDataStateChange,
  autoProcessData = false,
  dataState: dataStateProp,
  initialSkip = DEFAULT_START_INDEX,
  initialTake = DEFAULT_PAGE_SIZE,
  initialFilter,
  initialSort,
  columns,
  error,
  noItemsMessage = "No items to show",
  filterable = true,
}: TableProps<T>) {
  const isControlled = dataStateProp !== undefined;
  // Give a warning if the component switches between controlled and uncontrolled
  useControlledSwitchWarning(
    isControlled,
    "KendoTableClientState",
    "dataState"
  );

  const gridRef = useRef<GridHandle>(null);
  const EnterActivatableDataCell = useEnterActivatableDataCell(
    onRowClick,
    gridRef
  );

  // Internal state used when uncontrolled
  const [internalState, setInternalState] = useState<GridDataState>({
    skip: initialSkip,
    take: initialTake,
    filter: initialFilter ?? undefined,
    sort: initialSort ?? undefined,
  });

  const formattedData = useMemo(() => {
    // Check if data is of type DataResult or an array
    const dataArray = Array.isArray(data)
      ? data
      : ((data as DataResult)?.data ?? []);
    return convertKendoTableDates<T>(dataArray, columns);
  }, [data, columns]);

  // The effective state the Grid should use
  const effectiveState = isControlled ? dataStateProp : internalState;

  // Process the data using the effective data state (apply filtering, sorting, paging).
  // Date "equals" filters are expanded to a full-day range only for this processing
  // step, not in the state stored/handed back to the Grid: Kendo's built-in filter-row
  // controls expect exactly one FilterDescriptor per field, so persisting the expanded
  // (two-descriptor) form there breaks their ability to display and clear the filter.
  const processingFilter = effectiveState.filter
    ? (rewriteDateEqualsFilter(
        effectiveState.filter
      ) as CompositeFilterDescriptor)
    : effectiveState.filter;
  const processedData = process(formattedData, {
    ...effectiveState,
    filter: processingFilter,
  });

  // The handler that either calls parent or updates internal state
  const handleDataStateChange = (event: GridDataStateChangeEvent) => {
    if (isControlled) {
      onDataStateChange?.(event);
    } else {
      setInternalState(event.dataState);
    }
  };

  return (
    <>
      {/* Kendo's grid keyboard navigation lands on the first data cell,
          not the sortable header row, with no visual/AT indication that
          arrow keys reach it. This note makes that discoverable without
          being visible to mouse users. */}
      <span tabIndex={0} className="visually-hidden-focusable">
        This table has sortable columns. Use the arrow keys to move between
        cells; press the up arrow to reach the column headers, then Enter or
        Space to sort.
      </span>
      <Grid
        ref={gridRef}
        // Spread the state so Grid sees skip, take, sort, filter
        {...effectiveState}
        data={error ? null : processedData}
        showLoader={showLoader}
        onRowClick={onRowClick}
        onDataStateChange={handleDataStateChange}
        autoProcessData={autoProcessData}
        pageable={true}
        pager={AccessiblePager}
        filterable={filterable}
        sortable={true}
        navigatable={true}
        scrollable="scrollable"
      >
        <GridNoRecords>
          {
            // Kendo Grid doesn't have a specific error handling component,
            // so use GridNoRecords for both errors and no data situations
            error ? (
              <Alert variant="danger">
                <p className="mb-0">{error.message}</p>
              </Alert>
            ) : (
              noItemsMessage
            )
          }
        </GridNoRecords>
        {columns.map((column) => (
          <GridColumn
            key={column.field}
            {...column}
            cells={{
              ...column.cells,
              data:
                column.cells?.data ??
                EnterActivatableDataCell ??
                DefaultDataCell,
            }}
            className={
              onRowClick
                ? joinClasses("cursor-pointer", column.className)
                : column.className
            }
            width={column.width ?? DEFAULT_MIN_COLUMN_WIDTH}
          />
        ))}
      </Grid>
    </>
  );
}
